<template>
    <div class="head">
        <div class="title">{{ title }}</div>
        <GoHomeButton @click.prevent="onGoHomeButtonClick" />
        <UserMenuComponent />
    </div>

    <div class="room-plan-view-container">
        <RoomPlanViewComponent
            ref="roomPlanViewComponent"
            :class="{ hidden: isLoading || isReallocated }"
            :transferBillOptions="transferBillOptions"
            :scaleLandscapeView="true"
            @tableSelected="onTableSelected"
        />

        <div class="loading-placeholder" v-if="isLoading || isReallocated">
            <div class="loading">
                <div class="loader green"></div>
                {{ translations.general.loading }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { RoomPlanController } from "@/app/controller/RoomPlanController";
    import { appEvents } from "@/app/events/appEvents";
    import { trans } from "@/app/localization/Trans";
    import { Helper } from "@/app/ui/Helper";
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { ProfileDao } from "@/data/dao/service/ProfileDao";
    import type { IRoomPlanTable } from "@/data/model/Operator";
    import type { TransferBillOptions } from "@/data/model/Table";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, nextTick, onMounted, ref } from "vue";

    import GoHomeButton from "@/app/components/header/GoHomeButton.vue";
    import UserMenuComponent from "@/app/components/header/UserMenuComponent.vue";
    import RoomPlanViewComponent from "@/components/roomPlan/RoomPlanViewComponent.vue";

    const profileDao = Factory.getInstance<ProfileDao>(ProfileDao);

    const isLoading = ref(false);
    const isReallocated = ref(false);
    const transferBillOptions = ref<TransferBillOptions | undefined>(undefined);

    function onTableSelected(table: IRoomPlanTable) {
        const currentAllocation = TastierClient.context.currentAllocation;
        if (currentAllocation?.tableId == table.id && currentAllocation.orderBillId == null) {
            if (!Helper.isSingleService()) {
                RoomPlanController.onShowServices.dispatch();
            }
            return;
        }

        allocateToTable(table);
    }

    async function allocateToTable(table: IRoomPlanTable) {
        isLoading.value = true;
        isReallocated.value = false;

        try {
            const allocationSuccess = await TastierClient.context.allocate(
                table.locationId,
                table.tableNumber,
                undefined,
                false,
                undefined
            );
            // when allocate function is successful, the user will be redirected to main page by 'onAllocationChange' event
            isReallocated.value = allocationSuccess;
        } catch (ex) {
            logError(ex);
            Popup.show({ message: translations.allocation.allocationFailed });
        } finally {
            isLoading.value = false;
        }
    }

    const roomPlanViewComponent = ref<InstanceType<typeof RoomPlanViewComponent> | null>(null);

    // Header's title
    const title = computed<string>(() => {
        const serviceDesignation = Helper.getCurrentServiceDesignation();
        return serviceDesignation == null ? trans.enums.serviceType.RoomPlan : serviceDesignation;
    });

    // define functions
    async function initialize() {
        isReallocated.value = false;

        // use current allocation room or default room from operator settings
        const currentAllocation = TastierClient.context.currentAllocation;
        if (currentAllocation != null) {
            await roomPlanViewComponent.value?.loadRoomPlan({
                location: {
                    id: currentAllocation.locationId,
                    name: currentAllocation.locationName,
                    internalName: currentAllocation.locationInternalName,
                },
                roomId: currentAllocation.roomId,
            });
            return;
        }

        if (TastierClient.identityContext.isWaiterOrAdmin()) {
            const settings = await profileDao.getOperatorSettings();
            if (settings?.location && settings.planRoom) {
                await roomPlanViewComponent.value?.loadRoomPlan({
                    location: settings.location,
                    room: settings.planRoom,
                });
                return;
            }
        }

        Popup.show({
            title: "No room plan found",
            message: "Could not find a room plan - no location or room provided",
        });
        logError("Could not find a room plan - no location or room provided");
    }

    // #region View visibility
    const areEventsInitialized = ref(false);
    const isViewVisible = ref(false);

    RoomPlanController.onShown.add(onBecomeVisible, this);
    RoomPlanController.onHidden.add(onBecomeInvisible, this);

    async function onBecomeVisible(this: any) {
        isViewVisible.value = true;
        await nextTick();

        // ts-1251: If we initialize this view too soon, it might throw 400/500 errors while loading fulfillment slots because of switching between the tables
        // we need to wait for app to be initialized before we initialize this view
        if (TastierClient.isInitialized) {
            initialize();
        } else {
            appEvents.tastierClient.initialized.addOnce(initialize);
        }

        if (!areEventsInitialized.value) {
            areEventsInitialized.value = true;
        }
    }

    function onBecomeInvisible(this: any) {
        isViewVisible.value = false;

        if (areEventsInitialized.value) {
            areEventsInitialized.value = false;
            appEvents.tastierClient.initialized.remove(initialize);
        }
    }

    onMounted(() => {
        onBecomeVisible();
    });
    // #endregion

    function onGoHomeButtonClick() {
        RoomPlanController.onShowServices.dispatch();
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .view.room-plan {
        display: flex;
        flex-direction: column;

        > .head {
            flex: 0 0 auto;
        }

        > .room-plan-view-container {
            flex: 1 1 auto;
            display: flex;

            > .room-plan-view {
                flex: 1 1 auto;

                @supports not (aspect-ratio: 1/1) {
                    .room {
                        width: 100%;
                        height: 0;
                        padding-bottom: 100%; /* (1/1) * 100% - fallback for aspect-ratio for older browsers */
                        &.landscape {
                            padding-bottom: 70%; /* (7/10) * 70% - fallback for aspect-ratio for older browsers */
                        }

                        &.portrait {
                            padding-bottom: 130%; /* (13/10) * 100% - fallback for aspect-ratio for older browsers */
                        }
                    }
                }

                .room.landscape {
                    .room-plan-table {
                        .inner {
                            font-size: 6px;

                            // dynamic font size based on screen width - backwards compatible
                            @for $i from 1 through 7 {
                                $width: 350px + ($i - 1) * 50px;
                                $size: 6px + ($i - 1) * 0.5px;

                                @media (min-width: $width) {
                                    font-size: $size;
                                }
                            }
                        }
                    }
                }

                .room.portrait,
                .room.square {
                    .room-plan-table {
                        .inner {
                            font-size: 9px;

                            // dynamic font size based on screen width - backwards compatible
                            @for $i from 1 through 7 {
                                $width: 350px + ($i - 1) * 50px;
                                $size: 9px + ($i - 1) * 0.5px;

                                @media (min-width: $width) {
                                    font-size: $size;
                                }
                            }
                        }
                    }
                }
            }

            > .loading-placeholder {
                position: relative;
                flex: 1 1 auto;
                display: flex;
                align-items: center;
                justify-content: center;
            }
        }
    }
</style>
